import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { assemblyStatusCodes } from "@/pages/mill/carriers/components/CarrierAssemblyStatusTag";
import { RollsetModalSelector } from "@/shared/selectors/rollset/RollsetModalSelector";
import { useGetReadyRollsets } from "@/shared/selectors/rollset/RollsetSelector";
import { ErrorModal, MesfModal, useMesfRealtime } from "@dexteel/mesf-core";
import { Edit } from "@mui/icons-material";
import {
	Box,
	Card,
	Divider,
	Grid2,
	IconButton,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { StandSpare } from "../../models/StandSpare";
import {
	getMillDetailsByStandId,
	replaceCarrierRolls,
} from "../../repositories/MillOverviewRepository";

type useMillDetailsByStandIdProps = {
	standId: number;
	show: boolean;
	onError: (error: Error) => void;
};

export const useMillDetailsByStandId = ({
	standId,
	show,
	onError,
}: useMillDetailsByStandIdProps) => {
	return useQuery<StandSpare[], Error>({
		queryKey: ["millDetails", standId, show],
		queryFn: ({ signal }) => {
			if (show) {
				return getMillDetailsByStandId({ standId }, signal);
			} else {
				return [];
			}
		},
		onError: onError,
	});
};

export const SparesModal = ({
	show,
	onHide,
	standId,
	standName,
	currentCampaignSectionId,
}: {
	show: boolean;
	onHide: (success: boolean) => void;
	standId: number;
	standName: string;
	currentCampaignSectionId: number | null;
}) => {
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [installedCarrier, setInstalledCarrier] = useState<StandSpare | null>(
		null,
	);
	const [spares, setSpares] = useState<StandSpare[] | null>(null);
	const [showReplaceRollsetModal, setShowReplaceRollsetModal] = useState(false);

	const {
		data: carriers,
		isLoading,
		isSuccess,
	} = useMillDetailsByStandId({
		standId: standId,
		show: show,
		onError: (error) => {
			setError(error.message);
		},
	});

	const { data: rollsets, isLoading: isLoadingRollsets } = useGetReadyRollsets({
		sectionId: currentCampaignSectionId,
	});

	const queryClient = useQueryClient();
	const upsert = useMutation(replaceCarrierRolls, {
		onSuccess: async () => {
			setNotificationMessage("The rolls were replaced successfully");

			await queryClient.invalidateQueries(["millOverviews"]);
			await queryClient.invalidateQueries(["millDetails", standId, show]);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") ==
				"M2.GetMillDetailsByStandId"
			) {
				await queryClient.invalidateQueries(["millDetails", standId, show]);
			}
		},
	});

	useEffect(() => {
		if (isSuccess) {
			setInstalledCarrier(
				carriers.find((c) => c.CarrierStatusCode === "I") || null,
			);
			setSpares(carriers.filter((c) => c.CarrierStatusCode !== "I"));
		}
	}, [isSuccess, carriers]);

	return (
		<>
			<MesfModal
				maxWidth="xl"
				open={show}
				handleClose={() => {
					onHide(false);
				}}
				id="spares_modal"
				title={`Spares for stand: ${standName}`}
			>
				<form>
					<MesfModal.Content style={{ padding: "20px 30px" }} dividers>
						{isLoading && <CenteredLazyLoading />}
						{!isLoading && (
							<Box style={{ width: "600px" }}>
								{installedCarrier?.CarrierStatusCode == null ? (
									<Card
										sx={{
											backgroundColor: "#f5f5f5",
											borderRadius: "12px",
											padding: (theme) => theme.spacing(2),
											textAlign: "center",
											marginBottom: (theme) => theme.spacing(2),
											border: "1px solid #e0e0e0",
										}}
										elevation={1}
									>
										<Typography
											color="textSecondary"
											style={{ fontSize: "1rem", fontWeight: "bold" }}
										>
											Empty
										</Typography>
									</Card>
								) : (
									<Box style={{ position: "relative" }}>
										<CarrierCard carrier={installedCarrier} />
										{installedCarrier?.CarrierStatusCode !== null && (
											<IconButton
												style={{
													position: "absolute",
													top: -12,
													right: -5,
													cursor: "pointer",
													backgroundColor: "white",
													borderRadius: "50%",
													width: 30,
													height: 30,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													zIndex: 1,
												}}
												onClick={(e) => {
													setShowReplaceRollsetModal(true);
												}}
												title="Replace rollset"
												disabled={isSubmitLoading}
											>
												<Edit style={{ fontSize: 20 }} color="primary" />
											</IconButton>
										)}
									</Box>
								)}

								{spares && spares.length > 0 && (
									<Box
										sx={{
											marginTop: (theme) => theme.spacing(2),
											maxHeight: "50vh",
											overflowY: "auto",
										}}
									>
										<Divider style={{ margin: "16px 0" }} />

										{spares.map(
											(spare, index) =>
												spare.PartId && <CarrierCard carrier={spare} />,
										)}
									</Box>
								)}
							</Box>
						)}
					</MesfModal.Content>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<RollsetModalSelector
				value={installedCarrier?.RollsetId ?? null}
				onChange={(rollsetId: number | null) => {
					if (rollsetId && installedCarrier?.CarrierInstanceId)
						upsert.mutate({
							carrierInstanceId: installedCarrier.CarrierInstanceId,
							rollsetId: rollsetId,
						});
				}}
				standId={standId}
				standName={standName}
				sectionId={currentCampaignSectionId}
				show={showReplaceRollsetModal}
				onHide={() => setShowReplaceRollsetModal(false)}
				rollsets={rollsets || []}
				isLoading={isLoadingRollsets}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};

const CarrierCard = ({ carrier }: { carrier: StandSpare }) => {
	return (
		<Card
			sx={{
				borderRadius: "8px",
				padding: (theme) => theme.spacing(2),
				marginBottom: (theme) => theme.spacing(1),
				background: assemblyStatusCodes.find(
					(a) => a.Code == carrier.CarrierStatusCode,
				)?.Color,
			}}
			elevation={1}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: (theme) => theme.spacing(1),
				}}
			>
				<Typography fontWeight="bold" fontSize="1rem" color="white">
					{carrier.PartName}
				</Typography>
				<Box
					sx={{
						fontSize: "0.9rem",
						fontWeight: "bold",
						borderRadius: "12px",
						color: "white",
					}}
				>
					{
						assemblyStatusCodes.find((a) => a.Code == carrier.CarrierStatusCode)
							?.Name
					}
				</Box>
			</Box>
			<Box
				sx={{
					fontSize: "0.8rem",
					color: "white",
				}}
				display="flex"
				justifyContent="space-between"
			>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Section
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						textAlign={"center"}
					>
						{carrier?.SectionName}
					</Typography>
				</Grid2>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Top Roll
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						sx={{
							textAlign: "center",
						}}
					>
						{carrier?.TopRollName}
					</Typography>
				</Grid2>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Bottom Roll
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						sx={{
							textAlign: "center",
						}}
					>
						{carrier?.BottomRollName}
					</Typography>
				</Grid2>
				<Grid2>
					<Typography
						fontSize={"0.875rem"}
						sx={{
							opacity: 0.9,
							marginBottom: (theme) => theme.spacing(0.5),
						}}
					>
						Diameter
					</Typography>
					<Typography
						fontSize={"0.875rem"}
						fontWeight={"bold"}
						sx={{
							textAlign: "center",
						}}
					>
						{carrier?.TopRollDiameter?.toFixed(3)}
					</Typography>
				</Grid2>
			</Box>
		</Card>
	);
};
